import { jsPDF } from "jspdf";
import {
  TEXT_X_MM, TEXT_WIDTH_MM, PAGE_HEIGHT_MM,
  mmToPt, drawHeader, drawFooter, ensureSpace, getTextRegion,
} from "./operationalReportsLayout";
import { fmtINR, fmtDatePdf, fmtTime } from "@emr/shared/india";

export interface DailyCollectionRow {
  id: string;
  number: string;
  patientName: string | null;
  clinicUhid: string | null;
  date: string;
  createdAt: string;
  paidPaise: number;
  totalPaise: number;
  status: string;
  method?: string;
}

export interface DailyCollectionData {
  from: string;
  to: string;
  rows: DailyCollectionRow[];
  days: { day: string; totalPaise: number; count: number }[];
}

export async function createDailyCollectionPdf(data: DailyCollectionData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(124, 45, 18);
  doc.text("Daily Collection Report", x, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 95, 88);
  doc.text(`${fmtDatePdf(data.from)} - ${fmtDatePdf(data.to)}`, x + w, y, { align: "right" });
  y += mmToPt(8);

  const grandTotal = data.days.reduce((s, d) => s + d.totalPaise, 0);

  const grouped = new Map<string, DailyCollectionRow[]>();
  for (const r of data.rows) {
    const day = (r.date ?? "").slice(0, 10);
    if (!grouped.has(day)) grouped.set(day, []);
    grouped.get(day)!.push(r);
  }
  const sortedDays = Array.from(grouped.keys()).sort();

  const colInv = mmToPt(35);
  const colPatient = mmToPt(70);
  const colTime = mmToPt(25);
  const colMethod = mmToPt(25);
  const colAmt = w - colInv - colPatient - colTime - colMethod;

  for (const day of sortedDays) {
    const dayRows = grouped.get(day)!;
    const dayTotal = dayRows.reduce((s, r) => s + r.paidPaise, 0);

    y = await ensureSpace(doc, y, mmToPt(8));
    doc.setFillColor(247, 240, 232);
    doc.rect(x, y, w, mmToPt(7), "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(124, 45, 18);
    doc.text(fmtDatePdf(day), x + mmToPt(2), y + mmToPt(5));
    doc.text(`${dayRows.length} payments`, x + w - mmToPt(2), y + mmToPt(5), { align: "right" });
    y += mmToPt(8);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(124, 45, 18);
    let cx = x + mmToPt(2);
    doc.text("Invoice #", cx, y);
    cx += colInv;
    doc.text("Patient", cx, y);
    cx += colPatient;
    doc.text("Time", cx, y);
    cx += colTime;
    doc.text("Method", cx, y);
    // cx += colMethod was dead — "Amount" is drawn at a computed right edge
    // (x + w - 2mm), not relative to cx.
    doc.text("Amount", x + w - mmToPt(2), y, { align: "right" });
    y += mmToPt(5);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(26, 20, 19);

    for (const r of dayRows) {
      y = await ensureSpace(doc, y, mmToPt(6));
      cx = x + mmToPt(2);
      doc.text(r.number ?? "", cx, y);
      cx += colInv;
      const patientLines = doc.splitTextToSize(r.patientName ?? "—", colPatient - mmToPt(2));
      doc.text(patientLines[0] ?? "—", cx, y);
      cx += colPatient;
      doc.text(r.createdAt ? fmtTime(r.createdAt) : "—", cx, y);
      cx += colTime;
      doc.text(r.method ?? "Cash", cx, y);
      // cx += colMethod was dead — same right-edge anchoring as the header.
      doc.text(fmtINR(r.paidPaise), x + w - mmToPt(2), y, { align: "right" });
      y += mmToPt(5);
    }

    y += mmToPt(2);
    y = await ensureSpace(doc, y, mmToPt(6));
    doc.setDrawColor(200, 190, 180);
    doc.line(x, y, x + w, y);
    y += mmToPt(4);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Day Total", x + w - mmToPt(60), y);
    doc.text(fmtINR(dayTotal), x + w - mmToPt(2), y, { align: "right" });
    y += mmToPt(8);
  }

  y = await ensureSpace(doc, y, mmToPt(10));
  doc.setFillColor(124, 45, 18);
  doc.rect(x, y, w, mmToPt(8), "F");
  doc.setTextColor(251, 247, 240);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Grand Total", x + mmToPt(2), y + mmToPt(6));
  doc.text(fmtINR(grandTotal), x + w - mmToPt(2), y + mmToPt(6), { align: "right" });

  return doc;
}

export interface NoShowRow {
  id: string;
  patientName: string | null;
  clinicUhid: string | null;
  phone: string | null;
  slot: string | null;
  arrivalTime: string;
  reason: string | null;
}

export interface NoShowsData {
  from: string;
  to: string;
  rows: NoShowRow[];
}

export async function createNoShowsPdf(data: NoShowsData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(124, 45, 18);
  doc.text("No-Shows Report", x, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 95, 88);
  doc.text(`${fmtDatePdf(data.from)} - ${fmtDatePdf(data.to)}`, x + w, y, { align: "right" });
  y += mmToPt(8);

  const colDate = mmToPt(30);
  const colSlot = mmToPt(20);
  const colPatient = mmToPt(70);
  const colUhid = mmToPt(30);
  const colPhone = mmToPt(36);
  const colReason = w - colDate - colSlot - colPatient - colUhid - colPhone;

  doc.setFillColor(247, 240, 232);
  doc.rect(x, y, w, mmToPt(7), "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(124, 45, 18);
  let cx = x + mmToPt(2);
  doc.text("Date", cx, y + mmToPt(5));
  cx += colDate;
  doc.text("Slot", cx, y + mmToPt(5));
  cx += colSlot;
  doc.text("Patient", cx, y + mmToPt(5));
  cx += colPatient;
  doc.text("UHID", cx, y + mmToPt(5));
  cx += colUhid;
  doc.text("Phone", cx, y + mmToPt(5));
  cx += colPhone;
  doc.text("Reason", cx, y + mmToPt(5));
  y += mmToPt(8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(26, 20, 19);

  for (const r of data.rows) {
    y = await ensureSpace(doc, y, mmToPt(6));
    cx = x + mmToPt(2);
    doc.text(r.arrivalTime ? fmtDatePdf(r.arrivalTime) : "—", cx, y);
    cx += colDate;
    doc.text(r.slot ?? "—", cx, y);
    cx += colSlot;
    const patientLines = doc.splitTextToSize(r.patientName ?? "—", colPatient - mmToPt(2));
    doc.text(patientLines[0] ?? "—", cx, y);
    cx += colPatient;
    doc.text(r.clinicUhid ?? "—", cx, y);
    cx += colUhid;
    doc.text(r.phone ?? "—", cx, y);
    cx += colPhone;
    const reasonLines = doc.splitTextToSize(r.reason ?? "—", colReason - mmToPt(2));
    doc.text(reasonLines[0] ?? "—", cx, y);
    y += mmToPt(5);
  }

  if (data.rows.length === 0) {
    doc.setTextColor(150, 140, 130);
    doc.text("No no-shows in this period.", x, y);
  }

  y += mmToPt(4);
  y = await ensureSpace(doc, y, mmToPt(6));
  doc.setDrawColor(200, 190, 180);
  doc.line(x, y, x + w, y);
  y += mmToPt(5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(124, 45, 18);
  doc.text("Total no-shows", x + mmToPt(2), y);
  doc.text(String(data.rows.length), x + w - mmToPt(2), y, { align: "right" });

  return doc;
}

export interface UpcomingApptRow {
  id: string;
  patientName: string | null;
  clinicUhid: string | null;
  phone: string | null;
  slot: string | null;
  arrivalTime: string;
  status: string;
  doctorName: string | null;
  reason: string | null;
}

export interface UpcomingApptsData {
  from: string;
  to: string;
  rows: UpcomingApptRow[];
}

export async function createUpcomingApptsPdf(data: UpcomingApptsData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  await drawHeader(doc);
  await drawFooter(doc);

  const region = getTextRegion(doc);
  let y = region.y;
  const x = region.x;
  const w = region.width;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(124, 45, 18);
  doc.text("Upcoming Appointments", x, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 95, 88);
  doc.text(`${fmtDatePdf(data.from)} - ${fmtDatePdf(data.to)}`, x + w, y, { align: "right" });
  y += mmToPt(8);

  const colDate = mmToPt(28);
  const colTime = mmToPt(20);
  const colSlot = mmToPt(18);
  const colPatient = mmToPt(60);
  const colUhid = mmToPt(28);
  const colPhone = mmToPt(32);
  const colDoctor = w - colDate - colTime - colSlot - colPatient - colUhid - colPhone;

  doc.setFillColor(247, 240, 232);
  doc.rect(x, y, w, mmToPt(7), "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(124, 45, 18);
  let cx = x + mmToPt(2);
  doc.text("Date", cx, y + mmToPt(5));
  cx += colDate;
  doc.text("Time", cx, y + mmToPt(5));
  cx += colTime;
  doc.text("Slot", cx, y + mmToPt(5));
  cx += colSlot;
  doc.text("Patient", cx, y + mmToPt(5));
  cx += colPatient;
  doc.text("UHID", cx, y + mmToPt(5));
  cx += colUhid;
  doc.text("Phone", cx, y + mmToPt(5));
  cx += colPhone;
  doc.text("Doctor", cx, y + mmToPt(5));
  y += mmToPt(8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(26, 20, 19);

  for (const r of data.rows) {
    y = await ensureSpace(doc, y, mmToPt(6));
    cx = x + mmToPt(2);
    doc.text(r.arrivalTime ? fmtDatePdf(r.arrivalTime) : "—", cx, y);
    cx += colDate;
    doc.text(r.arrivalTime ? fmtTime(r.arrivalTime) : "—", cx, y);
    cx += colTime;
    doc.text(r.slot ?? "—", cx, y);
    cx += colSlot;
    const patientLines = doc.splitTextToSize(r.patientName ?? "—", colPatient - mmToPt(2));
    doc.text(patientLines[0] ?? "—", cx, y);
    cx += colPatient;
    doc.text(r.clinicUhid ?? "—", cx, y);
    cx += colUhid;
    doc.text(r.phone ?? "—", cx, y);
    cx += colPhone;
    const docLines = doc.splitTextToSize(r.doctorName ?? "—", colDoctor - mmToPt(2));
    doc.text(docLines[0] ?? "—", cx, y);
    y += mmToPt(5);
  }

  if (data.rows.length === 0) {
    doc.setTextColor(150, 140, 130);
    doc.text("No upcoming appointments in this period.", x, y);
  }

  y += mmToPt(4);
  y = await ensureSpace(doc, y, mmToPt(6));
  doc.setDrawColor(200, 190, 180);
  doc.line(x, y, x + w, y);
  y += mmToPt(5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(124, 45, 18);
  doc.text("Total appointments", x + mmToPt(2), y);
  doc.text(String(data.rows.length), x + w - mmToPt(2), y, { align: "right" });

  return doc;
}
